
import Splide from '@splidejs/splide';

export const createCarousel = (element, options) => {

    // Get slides
    const slides = element.querySelectorAll(options.dataAttributes.slidesSelector);
    if (!slides.length) {
        console.error("No slides found for carousel component");
        return;
    }

    // Create the Carousel
    const carousel = new Carousel(element, slides, options);
    return carousel;
};


class Carousel {

    options = {};
    originalOptions = {};
    element = null;
    splide = null;
    slides = null;

    constructor(element, slides, options) {

        // Set the element
        this.element = element;
        this.slides = slides;
        this.originalOptions = options;
        this.options = options;
        
        this.setSplideSlides();
        
        // Only create the carousel if it should created
        if(this.shouldCreate()) {
            this.createSplideCarousel();
        }
        
        const resizeWindow = () => {
            this.resizeWindow();
        };
        window.addEventListener('resize', resizeWindow.bind(this));
    }

    /**
     * Create the Splide carousel
     * @returns {void}
     */
    createSplideCarousel() {
        const options = this.options;
        const splide = new Splide(this.element, options);
        this.splide = splide;

        // Set class for carousel
        if (this.options.class) {
            if (this.options.class instanceof Array) {
                for (const cls of this.options.class) {
                    this.splide.root.classList.add(cls);
                }
            } else {
                this.splide.root.classList.add(this.options.class);
            }
        }

        const refresh = () => {
            this.updateSplideCarousel();
        };
        
        this.splide.on('refresh', refresh.bind(this));
        this.splide.mount();
        this.splide.on('moved', refresh.bind(this));
        this.splide.on('resize', refresh.bind(this));
    }


    /**
     * Set the slides for the carousel
     * return {void}
     */
    setSplideSlides() {
        for (const slideContent of this.slides) {
            const slide = document.createElement('li');
            slide.classList.add('splide__slide');
            slide.appendChild(slideContent);
            const list = this.element.querySelector('.splide__list');
            list.appendChild(slide);
        }
    }
    
    /**
     * Resize window event
     * @returns {void}
     */
    resizeWindow() {
        // Get the current state of the carousel
        const create = this.shouldCreate();
        if(create && (!this.splide || this.splide.state.is(Splide.STATES.DESTROYED))) {
            this.createSplideCarousel();
            
        }
        else if(!create && this.splide && !this.splide.state.is(Splide.STATES.DESTROYED)) {
            this.splide.destroy(false);
        }
    }

    /**
     * Check if the carousel should be created
     */
    shouldCreate() {

        const minSplides = this.options.slidesMin ?? 2;

        // Check if the carousel has at least 2 slide
        if(this.slides.length < minSplides) {
            return false;
        }
    
        // Get the total width of the slides
        const slidesGap = this.options.slidesGap ?? 0;
        let totalWidth = slidesGap * (this.slides.length - 1);
        for (const slide of this.slides) {
            totalWidth += slide.offsetWidth;
        }

        return totalWidth > this.element.offsetWidth;
    }


    /*
    * Update the carousel elements
    * @returns {void}
    * */
    updateSplideCarousel() {
        this.setCarouselHeight();
        this.setArrowsPosition();
    }


    /**
     * Set the carousel height based on the tallest slide
     * @returns {void}
     */
    setCarouselHeight() {
        const { root } = this.splide.Components.Elements;

        // Get the current slides
        const currentSlides = root.querySelectorAll('.splide__slide.is-active.is-visible');

        // Get the height of the tallest slide
        let height = 0;
        for (const slide of currentSlides) {

            // set slide height
            const elHeight = slide.offsetHeight > 0 ? slide.offsetHeight : 0;
            if (elHeight > height) {
                height = elHeight;
            }
        }

        // Set viewport height
        if (height > 0) {
            const trackWrapper = root.querySelector('.splide__track-wrapper');
            trackWrapper.style.height = `${height}px`;
        }
    };


    /**
     * Set arrows vertical position based on a given element
     * @returns {void}
     */
    setArrowsPosition() {
        const { arrows } = this.splide.Components.Arrows;

        const arrowsCenterCurrentItem = 'arrowsCenterCurrentItem' in this.options.dataAttributes;
        const arrowsCenterSelector = this.options.dataAttributes?.arrowsCenterSelector;
        let arrowCenterElement = null;

        if(!arrowsCenterSelector) {
            return;
        }

        if(arrowsCenterCurrentItem) {
            const currentIndex = this.splide.Components.Controller.getIndex();
            const currentElement = this.splide.Components.Slides.getAt(currentIndex);
            arrowCenterElement = currentElement.slide.querySelector(arrowsCenterSelector);
        }
        else {
            arrowCenterElement = this.splide.root.querySelector(arrowsCenterSelector);
        }

        if (!arrowCenterElement) { return; }
        
        const arrowCenter = arrowCenterElement.offsetHeight > 0 ? arrowCenterElement.offsetHeight / 2 : 0;
        if (arrowCenter > 0) {
            for (const arrow of Object.values(arrows)) {
                arrow.style.top = `${arrowCenter}px`;
            }
        }
    }


    /**
     * Get the breakpoint options based on the container widt
     * @returns {Object} The options for the current width
     */
    getBreakpointOptions() {
        const width = this.element.offsetWidth ?? 0;
        const breakpoints = this.originalOptions.containerBreakpoints;

        // get the options for the current width
        let currentOptions = this.originalOptions;
        for (const breakpoint in breakpoints) {
            if (width >= breakpoint) {
                currentOptions = { ...currentOptions, ...breakpoints[breakpoint] };
            }
        }

        return currentOptions;
    }

}